// screens/DocumentViewScreen.js
//
// Public page that renders one of the legal PDFs (Aviso de Privacidad /
// Términos y Condiciones / Lineamientos) uploaded by an admin. The document
// to show is selected by the `docKey` prop, set per-route in App.js.

import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import Loader from "../components/Loader";
import { fetchSiteDocuments } from "../actions/siteDocumentActions";

const DocumentViewScreen = ({ docKey, title }) => {
  const dispatch = useDispatch();
  const { loading, error, documentsMap } = useSelector((s) => s.siteDocuments);

  useEffect(() => {
    dispatch(fetchSiteDocuments());
  }, [dispatch]);

  const doc = useMemo(
    () => (documentsMap ? documentsMap[docKey] : null),
    [documentsMap, docKey]
  );

  const pdfUrl = doc ? doc.secureUrl || doc.url : null;

  return (
    <div className="hadacy-docview">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;0,600&family=Jost:wght@200;300;400;500&display=swap');

        .hadacy-docview {
          --dark: #1a1410;
          --gold: #b8933a;
          --gold-light: #d4af6a;
          --muted: #7a6e62;
          --card-bg: #faf7f2;
          --hairline: rgba(184, 147, 58, 0.18);
          font-family: "Jost", sans-serif;
          color: var(--dark);
          max-width: 1000px;
          margin: 1.6rem auto 3rem;
          padding: 0 1.2rem;
        }
        .hadacy-docview__eyebrow {
          font-size: 0.66rem;
          letter-spacing: 0.42em;
          text-transform: uppercase;
          color: var(--gold);
          margin-bottom: 0.4rem;
        }
        .hadacy-docview__title {
          font-family: "Cormorant Garamond", serif;
          font-weight: 400;
          font-size: 2.1rem;
          line-height: 1.15;
          margin: 0 0 1rem 0;
        }
        .hadacy-docview__frame {
          width: 100%;
          height: 80vh;
          border: 1px solid var(--hairline);
          border-radius: 12px;
          background: var(--card-bg);
        }
        .hadacy-docview__actions { margin-bottom: 1rem; }
        .hadacy-docview__open {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.6rem 1.2rem;
          border-radius: 999px;
          background: var(--dark);
          color: var(--gold-light);
          font-size: 0.68rem;
          letter-spacing: 0.22em;
          text-transform: uppercase;
          text-decoration: none;
          transition: background .2s ease, color .2s ease;
        }
        .hadacy-docview__open:hover { background: var(--gold); color: var(--dark); }
        .hadacy-docview__empty {
          background: var(--card-bg);
          border: 1px solid var(--hairline);
          border-radius: 12px;
          padding: 2rem;
          text-align: center;
          color: var(--muted);
          font-size: 0.95rem;
          line-height: 1.6;
        }
      `}</style>

      <div className="hadacy-docview__eyebrow">HADACY</div>
      <h1 className="hadacy-docview__title">{title}</h1>

      {loading && !doc ? (
        <Loader />
      ) : pdfUrl ? (
        <>
          <div className="hadacy-docview__actions">
            <a
              className="hadacy-docview__open"
              href={pdfUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              <i className="fas fa-external-link-alt"></i> Abrir / Descargar PDF
            </a>
          </div>
          <iframe
            className="hadacy-docview__frame"
            src={pdfUrl}
            title={title}
          ></iframe>
        </>
      ) : (
        <div className="hadacy-docview__empty">
          {error
            ? "No se pudo cargar el documento en este momento. Intenta de nuevo más tarde."
            : "Este documento aún no está disponible."}
        </div>
      )}
    </div>
  );
};

export default DocumentViewScreen;
